import _ from 'lodash';
import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router';

const Jsonld = observer(
    ({
        type,
        title,
        description,
        image,
        publishedAt,
        jsonld: jsonldProps,
        keepTitle = false,
    }: {
        type?:
            | 'BlogPosting' // 글 상세
            | 'Blog' // 글 목록
            | 'Organization' // 조직 (홈페이지에 추가)
            | 'WebSite'; // 홈페이지
        title?: string;
        description?: string;
        image?: string;
        publishedAt?: string;
        jsonld?: any;
        keepTitle?: boolean;
    }) => {
        const baseUrl = 'https://haewao.com';
        const location = useLocation();

        const pathname = location.pathname || '';
        const fullUrl = `${baseUrl}${pathname}`;

        const serviceTitle = '해와오';

        let refinedTitle = title;
        if (title && title !== serviceTitle && !keepTitle) {
            refinedTitle = `${title} - ${serviceTitle}`;
        }

        // 태그 내에 들어갈 땐 공백을 정리해줌
        const refinedDescription = description ? description.replace(/\s+/g, ' ') : '';

        const logoUrl = 'https://static.haewao.com/images/logo/haewao.png';

        const jsonld = useMemo(() => {
            if (jsonldProps) {
                return jsonldProps;
            }

            if (type === 'WebSite') {
                return {
                    '@context': 'https://schema.org',
                    '@type': 'WebSite',
                    name: refinedTitle,
                    url: fullUrl,
                    logo: logoUrl,
                    description: description,
                    termsOfService: `https://haewao.com/terms`,
                    privacyPolicy: `https://haewao.com/privacy`,
                };
            } else if (type === 'Organization') {
                return {
                    '@context': 'https://schema.org',
                    '@type': 'Organization',
                    name: serviceTitle,
                    url: baseUrl,
                    logo: logoUrl,
                };
            } else if (type === 'Blog') {
                return {
                    '@context': 'https://schema.org',
                    '@type': 'Blog',
                    name: refinedTitle,
                    url: fullUrl,
                    description: refinedDescription,
                };
            } else if (type === 'BlogPosting') {
                return {
                    '@context': 'https://schema.org',
                    '@type': type,
                    headline: refinedTitle,
                    image: image || '',
                    publisher: {
                        '@type': 'Organization',
                        name: serviceTitle,
                        logo: {
                            '@type': 'ImageObject',
                            url: logoUrl,
                        },
                    },
                    datePublished: publishedAt,
                    description: refinedDescription,
                    url: fullUrl,
                    mainEntityOfPage: {
                        '@type': 'WebPage',
                        '@id': fullUrl,
                    },
                };
            } else {
                return null;
            }
        }, [type, title, image, publishedAt, refinedDescription, fullUrl, jsonldProps]);

        const jsonldString = useMemo(() => {
            if (!jsonld || _.isEmpty(jsonld)) {
                return null;
            }

            return JSON.stringify(
                _.extend(
                    {
                        '@context': 'https://schema.org',
                    },
                    jsonld,
                ),
            );
        }, [jsonld]);

        if (!jsonldString) {
            return null;
        }

        return (
            <Helmet>
                <script type="application/ld+json">{jsonldString}</script>
            </Helmet>
        );
    },
);

export default Jsonld;
